import { Component } from "@theme/component";
import {
  debounce,
  onDocumentLoaded,
  setHeaderMenuStyle,
} from "@theme/utilities";
import { MegaMenuHoverEvent } from "@theme/events";

/**
 * How long the pointer must rest on a left-nav row before that row's panel opens.
 * While the pointer is still traveling toward the open panel, the wait restarts
 * so a diagonal move can cross neighboring rows (for example Soccer → All Soccer
 * passing over Pickleball) without switching the panel. 300ms is long enough
 * to cross one neighboring row when aim detection does not extend the wait.
 */
const LEFT_NAV_HOVER_DELAY = 300;

/**
 * Extra pixels above and below the open panel when testing whether the pointer
 * is traveling toward it.
 */
const LEFT_NAV_AIM_TOLERANCE = 75;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 * @property {HTMLElement | null} activeLeftNavItem - The currently active submenu Left Nav item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ["overflowMenu"];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * @type {ResizeObserver | null}
   */
  #menuColumnObserver = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#onDocumentLoaded);
    window.addEventListener("resize", this.#resizeListener);
    this.overflowMenu?.addEventListener(
      "pointerleave",
      this.#overflowSubmenuListener,
    );
    this.#observeMenuColumns();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener("resize", this.#resizeListener);
    document.body.removeEventListener("pointermove", this.#onPointerMove);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener(
      "pointerleave",
      this.#overflowSubmenuListener,
    );
    this.#cleanupMutationObserver();
    this.#clearLeftNavHover();
    this.#menuColumnObserver?.disconnect();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
    this.#fitMenuColumns(true);
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
    activeLeftNavItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Pending left-nav row switch, held while the pointer may still be aiming
   * at the open panel.
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #leftNavHoverTimer;

  /**
   * Left-nav row waiting out the hover delay.
   * @type {HTMLElement | null}
   */
  #pendingLeftNavItem = null;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Pointer position from the previous move, used to tell a diagonal aim
   * into the open panel from a vertical scan of the left nav.
   * @type {{ x: number, y: number }}
   */
  #previousPointer = { x: 0, y: 0 };

  /**
   * Pointer samples taken since the current top-level menu item opened.
   * Aim detection waits for two samples so a stale position from the last
   * open menu cannot decide the first move.
   * @type {number}
   */
  #pointerSamples = 0;

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    if (this.#pointerSamples > 0) {
      this.#previousPointer.x = this.#lastPointer.x;
      this.#previousPointer.y = this.#lastPointer.y;
    }

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;
    this.#pointerSamples += 1;

    if (this.#pointerSamples > 1) this.#postponeLeftNavHover();

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = "false";
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest(".menu-list__list-item");
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(
          new PointerEvent("pointerenter", { bubbles: false }),
        );
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    this.#pointerSamples = 0;

    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener("pointermove", this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute(
      "data-submenu-overlap-bottom-row",
    );
    const boundary = isOverlap
      ? this.headerComponent?.querySelector(".header__row--top")
      : this.headerComponent;
    item.style.setProperty(
      "--box-height",
      `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`,
    );
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty("--box-height");
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (
      this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]')
    );
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return (
      this.refs.overflowMenu?.shadowRoot
        ?.querySelector('[part="overflow-list"]')
        ?.matches(":hover") ?? false
    );
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest("header-component"));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === "";

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = "false";
    }

    this.#clearLeftNavHover();

    this.#state.activeItem = item;
    this.ariaExpanded = "true";
    item.ariaExpanded = "true";

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = "";
      requestAnimationFrame(() => {
        this.#fitMenuColumns(false, submenu);
      });

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty(
                "--submenu-height",
                `${submenu.offsetHeight}px`,
              );
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, {
        childList: true,
        subtree: true,
      });

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty(
      "--submenu-height",
      `${finalHeight}px`,
    );
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty("--submenu-opacity", "1");
    this.#startPointerTracking(item, previouslyActiveItem);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu =
      event.relatedTarget instanceof Node &&
      menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node &&
      event.type === "blur" &&
      menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node &&
      event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(":hover"))
      return;

    this.headerComponent?.style.setProperty("--submenu-height", "0px");
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty("--submenu-opacity", "0");
    this.dataset.overflowExpanded = "false";

    const submenu = findSubmenu(item);

    document.body.removeEventListener("pointermove", this.#onPointerMove);
    this.#stopPointerTracking(item);
    this.#clearLeftNavHover();

    this.#state.activeItem = null;
    this.#state.activeLeftNavItem = null;
    this.ariaExpanded = "false";
    item.ariaExpanded = "false";

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu
      ?.querySelector("slot")
      ?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (
          link.querySelector('[ref="submenu[]"]')
        );
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty("display", "none");
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty("display");
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute(
      "data-submenu-overlap-bottom-row",
    );

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (
            this.headerComponent.querySelector(".header__row--top")
              ?.offsetHeight ?? 0
          )
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen
      ? 0
      : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty(
      "--full-open-header-height",
      `${fullOpenHeaderHeight}px`,
    );
  }

  /**
   * Images in the menu change how many column tracks are filled.
   */
  #onDocumentLoaded = () => {
    this.#preloadImages();
    this.#fitMenuColumns(true);
  };

  /**
   * Watch menu lists so a promo image or a newly opened panel can recenter.
   */
  #observeMenuColumns() {
    this.#menuColumnObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.target instanceof HTMLElement) {
          this.#fitMenuColumnCount(entry.target, false);
        }
      }
    });

    for (const list of this.querySelectorAll(".mega-menu__list")) {
      this.#menuColumnObserver.observe(list);
    }

    this.#fitMenuColumns(true);
  }

  /**
   * Size each mega menu to the columns that contain content.
   * A tall promo keeps later CSS column tracks empty while the list stays full width,
   * so 2–4 columns sit on the left. Lower the declared count to the filled tracks
   * and the existing width rule centers them. Pass reset after a viewport change
   * so the authored count in data-mega-menu-column-count is measured again.
   * @param {boolean} reset
   * @param {ParentNode} [root]
   */
  #fitMenuColumns(reset, root = this) {
    const lists =
      root instanceof Element && root.matches(".mega-menu__list")
        ? [root]
        : root.querySelectorAll(".mega-menu__list");

    for (const list of lists) {
      if (list instanceof HTMLElement) this.#fitMenuColumnCount(list, reset);
    }
  }

  /**
   * @param {HTMLElement} list
   * @param {boolean} reset
   */
  #fitMenuColumnCount(list, reset) {
    if (list.offsetWidth === 0) return;

    if (reset) {
      const authored = list.dataset.megaMenuColumnCount;

      if (authored) {
        list.style.setProperty("--mega-menu-column-count", authored);
      }
    }

    const filled = countFilledMenuColumns(list);
    if (filled < 1) return;

    const current = Number(
      list.style.getPropertyValue("--mega-menu-column-count"),
    );
    if (!Number.isFinite(current) || filled >= current) return;

    list.style.setProperty("--mega-menu-column-count", String(filled));
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute("loading"));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }

  /**
   * Activate the selected submenu left nav item.
   * Pointer hover waits so a move into the open panel can cross other rows.
   * Keyboard focus switches immediately.
   * @param {PointerEvent | FocusEvent} event
   */
  activateSubmenu(event) {
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    const item = findLeftNavItem(event.target);
    if (!item) return;

    const previouslyActiveItem = this.#activeLeftNavItem();

    if (item === previouslyActiveItem) {
      this.#clearLeftNavHover();
      return;
    }

    if (event.type === "focus") {
      this.#clearLeftNavHover();
      this.#openLeftNavItem(item, previouslyActiveItem);
      return;
    }

    this.#scheduleLeftNavHover(item);
  }

  /**
   * Drop a pending left-nav switch when the pointer leaves that row.
   * @param {PointerEvent} event
   */
  cancelLeftNavActivation(event) {
    if (!(event.target instanceof Element)) return;

    const item = findLeftNavItem(event.target);
    if (!item || item !== this.#pendingLeftNavItem) return;

    this.#clearLeftNavHover();
  }

  /**
   * Currently expanded left-nav row, from state or the open submenu.
   * @returns {HTMLElement | null}
   */
  #activeLeftNavItem() {
    if (this.#state.activeLeftNavItem?.isConnected) {
      return this.#state.activeLeftNavItem;
    }

    const expanded = this.querySelector(
      '[ref="leftnavitem"][aria-expanded="true"]',
    );

    return expanded instanceof HTMLElement ? expanded : null;
  }

  /**
   * Show one left-nav panel and hide the one it replaces.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} [previouslyActiveItem]
   */
  #openLeftNavItem(item, previouslyActiveItem = this.#activeLeftNavItem()) {
    if (!item || item === previouslyActiveItem) return;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = "false";
      const prevMenu = findLeftNavMenu(previouslyActiveItem);
      if (prevMenu) prevMenu.ariaHidden = "true";
    }

    this.#state.activeLeftNavItem = item;
    item.ariaExpanded = "true";
    const newMenu = findLeftNavMenu(item);
    if (newMenu) {
      newMenu.ariaHidden = "false";
      requestAnimationFrame(() => {
        this.#fitMenuColumns(false, newMenu);
      });
    }
  }

  /**
   * Hold a left-nav switch until the pointer rests on that row.
   * @param {HTMLElement} item
   */
  #scheduleLeftNavHover(item) {
    this.#clearLeftNavHover();
    this.#pendingLeftNavItem = item;
    this.#leftNavHoverTimer = setTimeout(() => {
      this.#commitPendingLeftNav(item);
    }, LEFT_NAV_HOVER_DELAY);
  }

  /**
   * Keep the open panel up while the pointer is still moving toward it.
   */
  #postponeLeftNavHover() {
    const item = this.#pendingLeftNavItem;
    if (!item || !this.#isPointerAimingAtOpenSubmenu()) return;

    const row = item.closest(".mega-menu__left-nav-item");
    if (!row?.matches(":hover")) return;

    this.#scheduleLeftNavHover(item);
  }

  /**
   * Open the pending row once the pointer has rested on it.
   * @param {HTMLElement} item
   */
  #commitPendingLeftNav(item) {
    this.#leftNavHoverTimer = undefined;
    this.#pendingLeftNavItem = null;

    const row = item.closest(".mega-menu__left-nav-item");
    if (!row?.matches(":hover")) return;

    this.#openLeftNavItem(item);
  }

  #clearLeftNavHover() {
    clearTimeout(this.#leftNavHoverTimer);
    this.#leftNavHoverTimer = undefined;
    this.#pendingLeftNavItem = null;
  }

  /**
   * Whether the latest pointer movement is headed into the open left-nav panel.
   * A vertical move down the row list does not extend the hover wait.
   * @returns {boolean}
   */
  #isPointerAimingAtOpenSubmenu() {
    if (this.#pointerSamples < 2) return false;

    const current = this.#lastPointer;
    const previous = this.#previousPointer;
    const dx = current.x - previous.x;
    const dy = current.y - previous.y;
    if (dx <= 0 || Math.hypot(dx, dy) < 2) return false;

    const submenu = findLeftNavMenu(this.#activeLeftNavItem());
    if (!submenu) return false;

    const rect = submenu.getBoundingClientRect();
    if (rect.width < 1 || rect.height < 1 || current.x >= rect.left) {
      return false;
    }

    const decreasingCorner = {
      x: rect.left,
      y: rect.top - LEFT_NAV_AIM_TOLERANCE,
    };
    const increasingCorner = {
      x: rect.left,
      y: rect.bottom + LEFT_NAV_AIM_TOLERANCE,
    };
    const decreasingSlope = slopeBetween(current, decreasingCorner);
    const increasingSlope = slopeBetween(current, increasingCorner);
    const prevDecreasingSlope = slopeBetween(previous, decreasingCorner);
    const prevIncreasingSlope = slopeBetween(previous, increasingCorner);

    if (
      !Number.isFinite(decreasingSlope) ||
      !Number.isFinite(increasingSlope) ||
      !Number.isFinite(prevDecreasingSlope) ||
      !Number.isFinite(prevIncreasingSlope)
    ) {
      return false;
    }

    return (
      decreasingSlope < prevDecreasingSlope &&
      increasingSlope > prevIncreasingSlope
    );
  }
}

if (!customElements.get("header-menu")) {
  customElements.define("header-menu", HeaderMenu);
}

/**
 * How many CSS column tracks in a mega menu list contain a column.
 * Positions within a few pixels are the same track.
 * @param {HTMLElement} list
 * @returns {number}
 */
function countFilledMenuColumns(list) {
  const positions = [];

  for (const child of list.children) {
    if (!(child instanceof HTMLElement) || child.offsetWidth === 0) continue;

    const left = Math.round(child.getBoundingClientRect().left);
    const isNewColumn = positions.every(
      (position) => Math.abs(position - left) > 8,
    );

    if (isNewColumn) positions.push(left);
  }

  return positions.length;
}

/**
 * Slope from a pointer position to a corner of the open panel.
 * @param {{ x: number, y: number }} point
 * @param {{ x: number, y: number }} corner
 * @returns {number}
 */
function slopeBetween(point, corner) {
  return (corner.y - point.y) / (corner.x - point.x);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(
      element.parentElement?.querySelector('[slot="overflow"]'),
    );
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}

/**
 * Find the closest left nav item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findLeftNavItem(element) {
  const leftnavitem = element
    ?.closest(".mega-menu__left-nav-item")
    ?.querySelector('[ref="leftnavitem"]');
  return leftnavitem instanceof HTMLElement ? leftnavitem : null;
}

/**
 * Find the closest leftnavmenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findLeftNavMenu(element) {
  const index = element?.getAttribute("data-left-nav-index");
  const leftnavmenu = element
    ?.closest('[ref="submenu[]"]')
    ?.querySelector(`[ref="leftnavmenu[]"][data-left-nav-index="${index}"]`);

  return leftnavmenu instanceof HTMLElement ? leftnavmenu : null;
}
